import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const initialTestimonials = [
  {
    _id: "1",
    authorName: "Sarah Johnson",
    content: "Absolutely fantastic service! The team was professional and delivered beyond our expectations. Highly recommend to anyone looking for quality work.",
    rating: 5,
    createdAt: "2026-01-15T00:00:00.000Z",
  },
  {
    _id: "2",
    authorName: "Rahul Sharma",
    content: "Great experience overall. The product quality is top notch and customer support was very responsive and helpful throughout the process.",
    rating: 4,
    createdAt: "2026-02-01T00:00:00.000Z",
  },
];

const emptyForm = { authorName: "", content: "", rating: 5 };

const StarRating = ({ rating, interactive = false, onRate }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={`transition-colors select-none ${
            interactive ? "cursor-pointer text-xl sm:text-2xl" : "text-sm sm:text-base"
          } ${star <= (hovered || rating) ? "text-yellow-400" : "text-gray-200"}`}
        >
          ★
        </span>
      ))}
    </div>
  );
};

export default function Testimonial() {
  const [testimonials, setTestimonials] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getallTestimonial = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/testimonial/get-all`);
      setTestimonials(res.data.data);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getallTestimonial();
  }, []);

  const filtered = testimonials.filter((t) => {
    const matchSearch =
      t.authorName.toLowerCase().includes(search.toLowerCase()) ||
      t.content.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === "All" ||
      (filter === "5 Stars" && t.rating === 5) ||
      (filter === "4 Stars" && t.rating === 4) ||
      (filter === "≤3 Stars" && t.rating <= 3);
    return matchSearch && matchFilter;
  });

  const avgRating =
    testimonials.length > 0
      ? (testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length).toFixed(1)
      : 0;

  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const openCreate = () => {
    setEditData(null);
    setForm(emptyForm);
    setModalOpen(true);
    setMobileMenuOpen(false);
  };

  const openEdit = (t) => {
    setEditData(t);
    setForm({ authorName: t.authorName, content: t.content, rating: t.rating });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.authorName.trim() || !form.content.trim()) return;

    try {
      setSaving(true);

      if (editData) {
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/testimonial/update/${editData._id}`,
          form
        );

        if (res.data.success) {
          setTestimonials(prev =>
            prev.map(t => (t._id === editData._id ? res.data.data : t))
          );
          toast.success("Testimonial updated");
        }
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/testimonial/create`,
          form
        );

        if (res.data.success) {
          setTestimonials(prev => [...prev, res.data.data]);
          toast.success("Testimonial created");
        }
      }

      setModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/testimonial/delete/${id}`);
      setTestimonials(prev => prev.filter(t => t._id !== id));
      toast.success("Deleted successfully");
      setDeleteTarget(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const avatarColors = [
    "bg-blue-500", "bg-purple-500", "bg-green-500",
    "bg-orange-500", "bg-pink-500", "bg-teal-500",
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-200 gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-lg sm:text-xl flex-shrink-0">
            💬
          </div>
          <div className="min-w-0 flex-1 sm:flex-none">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Testimonial Management</h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Manage all customer testimonials from one place</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-colors text-sm w-full sm:w-auto"
        >
          <span className="text-lg leading-none">+</span>
          <span className="sm:inline">Add Testimonial</span>
        </button>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-gray-400 text-lg">🔍</span>
            <input
              type="text"
              placeholder="Search by author or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400 min-w-0"
            />
          </div>
          
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden flex items-center justify-between w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <span className="text-gray-600">Filter: {filter}</span>
            <span className="text-gray-400">▼</span>
          </button>

          {/* Filter Options - Desktop */}
          <div className="hidden sm:flex items-center gap-1 border-l border-gray-200 pl-4">
            <span className="text-gray-400 mr-2">⚙️</span>
            {["All", "5 Stars", "4 Stars", "≤3 Stars"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 lg:px-3 py-1 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  filter === f
                    ? "text-blue-600 font-semibold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Mobile Filter Menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
              {["All", "5 Stars", "4 Stars", "≤3 Stars"].map((f) => (
                <button
                  key={f}
                  onClick={() => {
                    setFilter(f);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 ${
                    filter === f
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-600 border border-gray-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Table - Desktop */}
        <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[2fr_1fr_2.5fr_1fr_0.7fr] px-6 py-3 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span>Author</span>
            <span>Rating</span>
            <span>Message</span>
            <span>Date</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <div className="text-4xl mb-3">💬</div>
              <p className="font-medium text-gray-500">No testimonials found</p>
              <p className="text-sm mt-1">Try adjusting your search or add a new one</p>
            </div>
          ) : (
            filtered.map((t, i) => {
              const initials = t.authorName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
              const color = avatarColors[i % avatarColors.length];

              return (
                <div
                  key={t._id}
                  className={`grid grid-cols-[2fr_1fr_2.5fr_1fr_0.7fr] px-6 py-5 items-center ${
                    i < filtered.length - 1 ? "border-b border-gray-100" : ""
                  } hover:bg-gray-50 transition-colors`}
                >
                  <div className="flex items-center gap-3 pr-4">
                    <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                      {initials}
                    </div>
                    <span className="font-semibold text-gray-900 text-sm truncate">{t.authorName}</span>
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <StarRating rating={t.rating} />
                    <span className="text-xs text-gray-400">{t.rating} / 5</span>
                  </div>

                  <div className="pr-4">
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      "{t.content}"
                    </p>
                  </div>

                  <div className="text-xs text-gray-500">
                    {new Date(t.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => openEdit(t)}
                      className="text-blue-400 hover:text-blue-600 transition-colors p-1"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => setDeleteTarget(t)}
                      className="text-red-400 hover:text-red-600 transition-colors p-1"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Card View - Tablet & Mobile */}
        <div className="lg:hidden space-y-4">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
              <div className="text-4xl mb-3">💬</div>
              <p className="font-medium text-gray-500">No testimonials found</p>
              <p className="text-sm mt-1">Try adjusting your search or add a new one</p>
            </div>
          ) : (
            filtered.map((t, i) => {
              const initials = t.authorName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
              const color = avatarColors[i % avatarColors.length];

              return (
                <div
                  key={t._id}
                  className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${color} flex items-center justify-center text-white text-sm sm:text-base font-bold flex-shrink-0`}>
                        {initials}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{t.authorName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating rating={t.rating} />
                          <span className="text-xs text-gray-400">{t.rating}/5</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(t)}
                        className="text-blue-400 hover:text-blue-600 transition-colors p-2 bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => setDeleteTarget(t)}
                        className="text-red-400 hover:text-red-600 transition-colors p-2 bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3">
                    "{t.content}"
                  </p>

                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 border-t border-gray-100 pt-3 mt-1">
                    <span className="flex items-center gap-1">
                      <span>📅</span>
                      {new Date(t.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 sm:mt-6 px-4 sm:px-6 py-3 sm:py-4 bg-white rounded-xl border border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm text-gray-500">
          <span>Total: <strong className="text-gray-800">{testimonials.length}</strong></span>
          <span>Avg Rating: <strong className="text-yellow-500">★ {avgRating}</strong></span>
          <span className="hidden sm:inline">5-Star: <strong className="text-green-600">{testimonials.filter((t) => t.rating === 5).length}</strong></span>
          <span className="hidden md:inline">Last Updated: <strong className="text-gray-800">{today}</strong></span>
          
          {/* Mobile stats */}
          <div className="flex sm:hidden items-center gap-3 w-full justify-center border-t border-gray-100 pt-2 mt-1">
            <span>5-Star: <strong className="text-green-600">{testimonials.filter((t) => t.rating === 5).length}</strong></span>
            <span>Updated: <strong className="text-gray-800">{today}</strong></span>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal - Responsive */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl sm:mx-4 animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editData ? "Edit Testimonial" : "Add Testimonial"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-2"
              >
                ✕
              </button>
            </div>

            <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Author Name *
                </label>
                <input
                  value={form.authorName}
                  onChange={(e) => setForm({ ...form, authorName: e.target.value })}
                  placeholder="e.g. Sarah Johnson"
                  className="w-full border border-gray-200 rounded-lg px-3 py-3 sm:py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Rating
                </label>
                <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-3 sm:py-2.5">
                  <StarRating
                    rating={form.rating}
                    interactive
                    onRate={(r) => setForm({ ...form, rating: r })}
                  />
                  <span className="text-sm font-semibold text-yellow-500">{form.rating} / 5</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Message *
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Write the testimonial message..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-3 sm:py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition resize-none"
                />
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-3 sm:py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.authorName.trim() || !form.content.trim()}
                className="px-5 py-3 sm:py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition w-full sm:w-auto"
              >
                {saving ? "Saving..." : editData ? "Save Changes" : "Add Testimonial"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm - Responsive */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              🗑️
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Testimonial?</h3>
            <p className="text-sm text-gray-500 mb-6">
              You're about to remove{" "}
              <strong className="text-gray-800">{deleteTarget.authorName}'s</strong>{" "}
              testimonial. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget._id)}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition order-1 sm:order-2"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom animation styles */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}